import React, { useState, useEffect, createContext, useContext } from 'react';
import { setConfigInstance, updateApiPrefix } from './config-accessor';

export interface AppConfig {
    newApiPrefix: string;
    unAuthenticatedEntryPath: string;
    tourPath: string;
    locale: string;
    posUrl: string;
    orderManagementUrl: string;
    companyLogin: boolean;
    [key: string]: any;
}

const ConfigContext = createContext<AppConfig | null>(null);

export const useConfig = () => {
    const config = useContext(ConfigContext);
    if (!config) {
        throw new Error('useConfig must be used within a ConfigProvider');
    }
    return config;
};

interface ConfigProviderProps {
    children: React.ReactNode;
}

export const ConfigProvider: React.FC<ConfigProviderProps> = ({ children }) => {
    const [config, setConfig] = useState<AppConfig | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        const loadConfig = async () => {
            try {
                const response = await fetch('/config.json');
                if (!response.ok) {
                    throw new Error(`Failed to load config: ${response.status} ${response.statusText}`);
                }
                const loadedConfig = await response.json();

                // Check if user is already logged in with a company
                const storedCompanyName = localStorage.getItem('companyName');
                if (storedCompanyName && loadedConfig.companyLogin) {
                    try {
                        const companyName = atob(storedCompanyName);
                        // Update the API prefix with the stored company name
                        const baseUrl = loadedConfig.newApiPrefix;

                        loadedConfig.newApiPrefix = `${baseUrl}/${companyName}`;

                        // Update posUrl and orderManagementUrl with company name
                        loadedConfig.posUrl = `${loadedConfig.posUrl}/${companyName}`;
                        loadedConfig.orderManagementUrl = `${loadedConfig.orderManagementUrl}/${companyName}`;
                    } catch (e) {
                        console.warn('Failed to decode stored company name');
                    }
                }

                setConfig(loadedConfig);
                setConfigInstance(loadedConfig);
            } catch (err) {
                console.error('Error loading configuration:', err);
                setError(err instanceof Error ? err : new Error(String(err)));
            } finally {
                setLoading(false);
            }
        };

        loadConfig();
    }, []);

    if (loading) {
        return <div>Loading configuration...</div>;
    }

    if (error || !config) {
        return <div>Error loading configuration: {error?.message || 'Unknown error'}</div>;
    }

    return (
        <ConfigContext.Provider value={config}>
            {children}
        </ConfigContext.Provider>
    );
};
